<script>
  import Icon from "@iconify/svelte";
  import { onMount } from "svelte";
  import { api, getToken } from "../lib/api.svelte.js";
  import { push } from "svelte-spa-router";
  import { t } from "../lib/i18n.svelte.js";
  import { addToast } from "../components/Toast.svelte";
  import { getTheme } from "../lib/stores/theme.svelte.js";
  import Layout from "../components/Layout.svelte";
  import hljs from "highlight.js/lib/core";
  import xml from "highlight.js/lib/languages/xml";

  hljs.registerLanguage("xml", xml);

  const methods = [
    { key: "url",        labelKey: "site_create.from_url",     icon: "mdi:link-variant",       descKey: "site_create.from_url_desc" },
    { key: "storage",    labelKey: "site_create.from_storage", icon: "mdi:folder-outline",     descKey: "site_create.from_storage_desc" },
    { key: "html_file",  labelKey: "site_create.upload_html",  icon: "mdi:file-code-outline",  descKey: "site_create.upload_html_desc" },
    { key: "html_paste", labelKey: "site_create.paste_html",   icon: "mdi:code-tags",          descKey: "site_create.paste_html_desc" },
    { key: "zip",        labelKey: "site_create.upload_zip",   icon: "mdi:folder-zip-outline", descKey: "site_create.upload_zip_desc" },
    { key: "api",        labelKey: "site_create.via_api",      icon: "mdi:cloud-braces",       descKey: "site_create.via_api_desc" },
  ];

  let step = $state(1);
  let method = $state("");
  let slug = $state("");
  let title = $state("");
  let description = $state("");
  let submitting = $state(false);

  let url = $state("");
  let storageFolder = $state("");
  let storageFolders = $state([]);
  let html = $state("");
  let file = $state(null);
  let dragOver = $state(false);
  let baseDomain = $state("");
  let isDark = $derived(getTheme() === "dark");

  let highlightedHtml = $derived.by(() => {
    if (!html) return "";
    try { return hljs.highlight(html, { language: "xml" }).value; } catch { return html; }
  });

  onMount(async () => {
    try {
      const setup = await api.get("/setup");
      const bd = (setup?.env || []).find(e => e.name === "BASE_DOMAIN");
      if (bd) baseDomain = bd.value;
    } catch {}
    try {
      const data = await api.get("/storage");
      storageFolders = (data?.entries || []).filter(e => e.is_dir).map(e => e.name);
    } catch {}
    history.pushState(null, "", location.href);
    const onPop = () => { if (step > 1) { step = 1; history.pushState(null, "", location.href); } };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  });

  let slugPreview = $derived(slug ? `${slug}.${baseDomain || "ui.example.com"}` : "");

  let canSubmit = $derived.by(() => {
    if (!slug.trim() || !title.trim() || !method) return false;
    if (method === "url" && !url.trim()) return false;
    if (method === "storage" && !storageFolder) return false;
    if (method === "html_paste" && !html.trim()) return false;
    if ((method === "html_file" || method === "zip") && !file) return false;
    return true;
  });

  function selectMethod(key) { method = key; step = 2; }

  function onTitleInput() {
    if (!slug || slug === slugify(title.slice(0, -1))) slug = slugify(title);
  }
  function slugify(s) { return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""); }
  function handleDrop(e) { e.preventDefault(); dragOver = false; file = e.dataTransfer?.files?.[0] || null; }
  function handleDragOver(e) { e.preventDefault(); dragOver = true; }
  function handleFileInput(e) { file = e.target.files?.[0] || null; }

  async function submit() {
    if (!canSubmit || submitting) return;
    submitting = true;
    try {
      if (method === "html_paste" || method === "url" || method === "storage") {
        const body = { type: method === "url" ? "figma_url" : method, slug, title, description };
        if (method === "html_paste") body.html = html;
        if (method === "url") body.url = url;
        if (method === "storage") body.storage_folder = storageFolder;
        await api.post("/sites", body);
      } else {
        const fd = new FormData();
        fd.append("type", method); fd.append("slug", slug);
        fd.append("title", title); fd.append("description", description);
        fd.append("file", file);
        await api.post("/sites", fd);
      }
      addToast(t("site_create.created"), "success");
      push("/sites");
    } catch (err) { addToast(err.message || t("site_create.failed"), "error"); }
    finally { submitting = false; }
  }

  let selectedMethod = $derived(methods.find(m => m.key === method));
  let isFileMethod = $derived(method === "html_file" || method === "zip");

  let liveHost = $derived(location.origin);
  let liveKey = $derived(getToken() || "YOUR_API_KEY");
  let domain = $derived(baseDomain || "ui.example.com");

  let llmPrompt = $derived(`SNAPSHOT API — Publish a static microsite in one request.

Server: ${liveHost}
Auth header: Authorization: Bearer ${liveKey}

METHOD 1 — Raw HTML (best for AI-generated content):
curl -X POST ${liveHost}/sites \\
  -H "Authorization: Bearer ${liveKey}" \\
  -H "Content-Type: application/json" \\
  -d '{"type":"html_paste","slug":"my-site","title":"My Site","html":"<!DOCTYPE html><html><body>Hello</body></html>"}'

METHOD 2 — Import from URL:
curl -X POST ${liveHost}/sites \\
  -H "Authorization: Bearer ${liveKey}" \\
  -H "Content-Type: application/json" \\
  -d '{"type":"figma_url","slug":"my-site","title":"My Site","url":"https://example.com"}'

METHOD 3 — Upload .html file:
curl -X POST ${liveHost}/sites \\
  -H "Authorization: Bearer ${liveKey}" \\
  -F type=html_file -F slug=my-site -F title="My Site" -F file=@index.html

METHOD 4 — Upload .zip archive:
curl -X POST ${liveHost}/sites \\
  -H "Authorization: Bearer ${liveKey}" \\
  -F type=zip -F slug=my-site -F title="My Site" -F file=@site.zip

The slug becomes the subdomain: my-site.${domain}
Slug must be lowercase, alphanumeric, hyphens only.
201 = success. 4xx = error with {"error":"..."}.
For AI agents: build HTML, POST with type=html_paste.`);
</script>

<svelte:head>
  {#if isDark}
    <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.9.0/styles/github-dark.min.css" />
  {:else}
    <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.9.0/styles/github.min.css" />
  {/if}
</svelte:head>

<Layout>
  <!-- Step 1: Choose method — 2x3 grid, vertically centred -->
  {#if step === 1}
    <div class="flex items-center justify-center px-4" style="min-height: calc(100vh - 160px);">
      <div class="w-full max-w-5xl">
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {#each methods as m}
            {@const active = method === m.key}
            <button
              type="button"
              class="group ui-card flex flex-col items-center text-center p-8 transition-all duration-200 cursor-pointer
                {active ? 'ring-1 ring-white/25' : 'hover:ring-1 hover:ring-white/10'}"
              onclick={() => selectMethod(m.key)}
            >
              <Icon
                icon={m.icon}
                class="w-12 h-12 mb-4 transition-colors duration-200
                  {active ? '' : 'text-zinc-400 group-hover:text-zinc-200'}"
                style={active ? "color: var(--snap-accent-hex)" : ""}
              />
              <p class="text-base font-medium mb-1 transition-colors {active ? 'text-white' : 'text-zinc-300 group-hover:text-white'}">{t(m.labelKey)}</p>
              <p class="text-sm leading-relaxed transition-colors {active ? 'text-zinc-400' : 'text-zinc-500 group-hover:text-zinc-400'}">{t(m.descKey)}</p>
            </button>
          {/each}
        </div>
      </div>
    </div>
  {/if}

  <!-- Step 2: Details — use full width -->
  {#if step === 2}
    <!-- Method badge + back -->
    <div class="flex items-center gap-3 mb-6 px-2">
      <button type="button" class="text-zinc-400 hover:text-white transition-colors" onclick={() => (step = 1)} title="Choose different method">
        <Icon icon="mdi:arrow-left" class="w-5 h-5" />
      </button>
      {#if selectedMethod}
        <Icon icon={selectedMethod.icon} class="w-5 h-5" style="color: var(--snap-accent-hex)" />
        <span class="text-sm text-zinc-200 font-medium">{t(selectedMethod.labelKey)}</span>
      {/if}
    </div>

    <!-- Paste HTML: side-by-side -->
    {#if method === "html_paste"}
      <div class="flex flex-col lg:flex-row gap-5 px-2" style="min-height: calc(100vh - 180px);">
        <!-- Left: form -->
        <div class="lg:w-[35%] shrink-0">
          <div class="ui-card p-6 flex flex-col">
            <div class="space-y-5 flex-1">
              <div>
                <label class="block text-sm text-zinc-300 mb-2">{t("site_create.title")}</label>
                <input type="text" bind:value={title} oninput={onTitleInput} class="w-full h-11 px-4 bg-black/20 border border-white/15 rounded text-base text-white placeholder-zinc-500 focus:outline-none focus:border-white/30" placeholder={t("site_create.title")} autofocus />
              </div>
              <div>
                <label class="block text-sm text-zinc-300 mb-2">{t("site_create.slug")}</label>
                <div class="flex">
                  <input type="text" bind:value={slug} class="w-full h-11 px-4 bg-black/20 border border-white/15 rounded rounded-r-none text-base text-white placeholder-zinc-500 focus:outline-none focus:border-white/30 flex-1" placeholder="my-client-site" />
                  <span class="h-11 px-3 flex items-center bg-white/[0.04] border border-l-0 border-white/15 rounded-r text-sm text-zinc-400 whitespace-nowrap">.{baseDomain || "ui.example.com"}</span>
                </div>
                {#if slugPreview}
                  <p class="text-xs text-zinc-500 mt-2"><Icon icon="mdi:open-in-new" class="w-3 h-3 inline" /> {slugPreview}</p>
                {/if}
              </div>
              <div>
                <label class="block text-sm text-zinc-300 mb-2">{t("site_create.description")} <span class="text-zinc-500">({t("site_create.description_optional")})</span></label>
                <input type="text" bind:value={description} class="w-full h-11 px-4 bg-black/20 border border-white/15 rounded text-base text-white placeholder-zinc-500 focus:outline-none focus:border-white/30" placeholder={t("site_create.description")} />
              </div>
            </div>
            <div class="pt-4">
              <button
                type="button"
                class="w-full ui-btn-accent !py-3 !text-sm !gap-2 !rounded {!canSubmit || submitting ? 'opacity-40 cursor-not-allowed' : ''}"
                onclick={submit} disabled={!canSubmit || submitting}
              >
                {#if submitting}
                  <Icon icon="mdi:loading" class="w-4 h-4 animate-spin" /> {t("site_create.creating")}
                {:else}
                  <Icon icon="mdi:check" class="w-4 h-4" /> {t("site_create.create_site")}
                {/if}
              </button>
            </div>
          </div>
        </div>

        <!-- Right: code editor -->
        <div class="flex-1 min-w-0">
          <div class="ui-card p-5 flex flex-col" style="min-height: 300px;">
            <label class="block text-sm text-zinc-300 mb-3">{t("site_create.html_source")}</label>
            <div class="flex-1 relative rounded overflow-hidden border border-white/[0.06] bg-black/20 min-h-[250px] lg:min-h-0">
              <textarea
                bind:value={html}
                class="absolute inset-0 w-full h-full px-4 py-3 text-sm font-mono text-transparent caret-white resize-none focus:outline-none z-10 bg-transparent"
                placeholder="<!DOCTYPE html>&#10;<html>&#10;  <head>...</head>&#10;  <body>...</body>&#10;</html>"
                spellcheck="false"
              ></textarea>
              <pre class="absolute inset-0 w-full h-full px-4 py-3 text-sm font-mono overflow-auto pointer-events-none m-0"><code class="hljs language-xml">{@html highlightedHtml || '<span class="text-zinc-600">&lt;!DOCTYPE html&gt;\n&lt;html&gt;\n  &lt;head&gt;...&lt;/head&gt;\n  &lt;body&gt;...&lt;/body&gt;\n&lt;/html&gt;</span>'}</code></pre>
            </div>
          </div>
        </div>
      </div>

    <!-- API method: two-column docs -->
    {:else if method === "api"}
      <div class="flex flex-col lg:flex-row gap-5 px-2" style="min-height: calc(100vh - 180px);">
        <!-- Left: human-readable API docs -->
        <div class="lg:w-1/2 shrink-0">
          <div class="ui-card p-6 h-full overflow-y-auto">
            <h2 class="text-lg font-semibold text-zinc-100 mb-2">{t("sites.api_reference")}</h2>
            <p class="text-sm text-zinc-400 mb-6">{t("sites.api_auth_note")}</p>
            <div class="text-xs bg-black/30 border border-white/10 rounded p-3 mb-6 flex items-center gap-2">
              <span class="text-zinc-500">{t("sites.api_your_key")}:</span>
              <code class="text-zinc-200 font-mono flex-1 truncate">{liveKey}</code>
              <button type="button" class="text-zinc-500 hover:text-white shrink-0" onclick={() => { navigator.clipboard.writeText(liveKey); addToast(t("common.copied"), "success"); }}>
                <Icon icon="mdi:content-copy" class="w-3.5 h-3.5" />
              </button>
            </div>

            <!-- Publish from URL -->
            <div class="mb-5 pb-5 border-b border-white/10">
              <div class="flex items-center gap-2 mb-2">
                <Icon icon="mdi:link-variant" class="w-4 h-4 text-zinc-400" />
                <h3 class="text-sm font-semibold text-zinc-200">{t("sites.api_from_url")}</h3>
              </div>
              <p class="text-sm text-zinc-400 mb-3">{t("sites.api_from_url_desc")}</p>
              <pre class="text-xs bg-black/30 border border-white/10 rounded p-3 overflow-x-auto"><code class="text-zinc-300">{`curl -X POST ${liveHost}/sites \\
  -H "Authorization: Bearer ${liveKey}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "type": "figma_url",
    "slug": "my-site",
    "title": "My Site",
    "url": "https://example.com/page"
  }'`}</code></pre>
            </div>

            <!-- Raw HTML -->
            <div class="mb-5 pb-5 border-b border-white/10">
              <div class="flex items-center gap-2 mb-2">
                <Icon icon="mdi:code-tags" class="w-4 h-4 text-zinc-400" />
                <h3 class="text-sm font-semibold text-zinc-200">{t("sites.api_raw_html")}</h3>
              </div>
              <p class="text-sm text-zinc-400 mb-3">{t("sites.api_raw_html_desc")}</p>
              <pre class="text-xs bg-black/30 border border-white/10 rounded p-3 overflow-x-auto"><code class="text-zinc-300">{`curl -X POST ${liveHost}/sites \\
  -H "Authorization: Bearer ${liveKey}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "type": "html_paste",
    "slug": "my-site",
    "title": "My Site",
    "html": "<!DOCTYPE html><html>...</html>"
  }'`}</code></pre>
            </div>

            <!-- HTML file upload -->
            <div class="mb-5 pb-5 border-b border-white/10">
              <div class="flex items-center gap-2 mb-2">
                <Icon icon="mdi:file-code-outline" class="w-4 h-4 text-zinc-400" />
                <h3 class="text-sm font-semibold text-zinc-200">{t("sites.api_upload_html")}</h3>
              </div>
              <p class="text-sm text-zinc-400 mb-3">{t("sites.api_upload_html_desc")}</p>
              <pre class="text-xs bg-black/30 border border-white/10 rounded p-3 overflow-x-auto"><code class="text-zinc-300">{`curl -X POST ${liveHost}/sites \\
  -H "Authorization: Bearer ${liveKey}" \\
  -F type=html_file \\
  -F slug=my-site \\
  -F title="My Site" \\
  -F file=@index.html`}</code></pre>
            </div>

            <!-- ZIP upload -->
            <div class="mb-2">
              <div class="flex items-center gap-2 mb-2">
                <Icon icon="mdi:folder-zip-outline" class="w-4 h-4 text-zinc-400" />
                <h3 class="text-sm font-semibold text-zinc-200">{t("sites.api_upload_zip")}</h3>
              </div>
              <p class="text-sm text-zinc-400 mb-3">{t("sites.api_upload_zip_desc")}</p>
              <pre class="text-xs bg-black/30 border border-white/10 rounded p-3 overflow-x-auto"><code class="text-zinc-300">{`curl -X POST ${liveHost}/sites \\
  -H "Authorization: Bearer ${liveKey}" \\
  -F type=zip \\
  -F slug=my-site \\
  -F title="My Site" \\
  -F file=@site.zip`}</code></pre>
            </div>
          </div>
        </div>

        <!-- Right: LLM-ready compressed prompt -->
        <div class="lg:w-1/2">
          <div class="ui-card p-6 h-full flex flex-col">
            <div class="flex items-center justify-between mb-2">
              <div>
                <h2 class="text-lg font-semibold text-zinc-100">{t("sites.api_llm_title")}</h2>
                <p class="text-sm text-zinc-500 mt-1">{t("sites.api_llm_desc")}</p>
              </div>
              <button
                type="button"
                class="ui-btn !text-xs !px-3 !py-1.5"
                onclick={() => { navigator.clipboard.writeText(llmPrompt); addToast(t("common.copied"), "success"); }}
              >
                <Icon icon="mdi:content-copy" class="w-3.5 h-3.5" /> {t("common.copy")}
              </button>
            </div>
            <div class="flex-1 relative rounded border border-white/10 bg-black/30 overflow-hidden mt-3">
              <pre class="absolute inset-0 p-4 text-xs font-mono text-zinc-300 leading-relaxed overflow-auto whitespace-pre-wrap m-0">{llmPrompt}</pre>
            </div>
          </div>
        </div>
      </div>

    <!-- All other methods: centred card -->
    {:else}
      <div class="max-w-2xl mx-auto">
        <div class="ui-card p-8">
          <div class="space-y-5">
            <div>
              <label class="block text-sm text-zinc-300 mb-2">{t("site_create.title")}</label>
              <input type="text" bind:value={title} oninput={onTitleInput} class="w-full h-11 px-4 bg-black/20 border border-white/15 rounded text-base text-white placeholder-zinc-500 focus:outline-none focus:border-white/30" placeholder={t("site_create.title")} autofocus />
            </div>

            <div>
              <label class="block text-sm text-zinc-300 mb-2">{t("site_create.slug")}</label>
              <div class="flex">
                <input type="text" bind:value={slug} class="w-full h-11 px-4 bg-black/20 border border-white/15 rounded rounded-r-none text-base text-white placeholder-zinc-500 focus:outline-none focus:border-white/30 flex-1" placeholder="my-client-site" />
                <span class="h-11 px-3 flex items-center bg-white/[0.04] border border-l-0 border-white/15 rounded-r text-sm text-zinc-400 whitespace-nowrap">.{baseDomain || "ui.example.com"}</span>
              </div>
              {#if slugPreview}
                <p class="text-xs text-zinc-500 mt-2"><Icon icon="mdi:open-in-new" class="w-3 h-3 inline" /> {slugPreview}</p>
              {/if}
            </div>

            <div>
              <label class="block text-sm text-zinc-300 mb-2">{t("site_create.description")} <span class="text-zinc-500">({t("site_create.description_optional")})</span></label>
              <input type="text" bind:value={description} class="w-full h-11 px-4 bg-black/20 border border-white/15 rounded text-base text-white placeholder-zinc-500 focus:outline-none focus:border-white/30" placeholder={t("site_create.description")} />
            </div>

            <!-- Method-specific -->
            {#if method === "url"}
              <div>
                <label class="block text-sm text-zinc-300 mb-2">{t("site_create.source_url")}</label>
                <input type="url" bind:value={url} class="w-full h-11 px-4 bg-black/20 border border-white/15 rounded text-base text-white placeholder-zinc-500 focus:outline-none focus:border-white/30" placeholder="https://example.com/page" />
              </div>

            {:else if method === "storage"}
              <div>
                <label class="block text-sm text-zinc-300 mb-2">{t("site_create.storage_folder")}</label>
                {#if storageFolders.length > 0}
                  <select class="w-full h-11 px-4 bg-black/20 border border-white/15 rounded text-base text-white focus:outline-none focus:border-white/30" bind:value={storageFolder}>
                    <option value="" class="bg-zinc-900">{t("site_create.choose_folder")}</option>
                    {#each storageFolders as f}<option value={f} class="bg-zinc-900">{f}/</option>{/each}
                  </select>
                {:else}
                  <p class="text-sm text-zinc-400">{t("site_create.no_folders")}</p>
                {/if}
              </div>

            {:else if isFileMethod}
              <div>
                <label class="block text-sm text-zinc-300 mb-2">{method === "zip" ? t("site_create.zip_archive") : t("site_create.html_file")}</label>
                <!-- svelte-ignore a11y_no_static_element_interactions -->
                <div
                  class="border-2 border-dashed rounded-lg p-10 text-center transition-colors {dragOver ? 'border-white/30 bg-white/[0.04]' : 'border-white/15 bg-white/[0.02]'}"
                  ondrop={handleDrop} ondragover={handleDragOver} ondragleave={() => (dragOver = false)}
                >
                  {#if file}
                    <div class="flex items-center justify-center gap-3">
                      <Icon icon="mdi:file-check-outline" class="w-6 h-6 text-zinc-300" />
                      <span class="text-base text-white">{file.name}</span>
                      <span class="text-sm text-zinc-400">({(file.size / 1024).toFixed(1)} KB)</span>
                      <button type="button" class="text-zinc-500 hover:text-white ml-2" onclick={() => (file = null)}>
                        <Icon icon="mdi:close" class="w-5 h-5" />
                      </button>
                    </div>
                  {:else}
                    <Icon icon="mdi:cloud-upload-outline" class="w-12 h-12 mx-auto text-zinc-500 mb-3" />
                    <p class="text-base text-zinc-300 mb-2">{t("site_create.drop_file")}</p>
                    <label class="inline-flex items-center gap-2 px-4 py-2 rounded border border-white/15 bg-white/[0.04] text-sm text-zinc-200 hover:bg-white/[0.08] cursor-pointer transition-colors">
                      <Icon icon="mdi:folder-open-outline" class="w-4 h-4" />
                      {t("site_create.browse_files")}
                      <input type="file" accept={method === "zip" ? ".zip" : ".html,.htm"} onchange={handleFileInput} class="hidden" />
                    </label>
                  {/if}
                </div>
              </div>
            {/if}

            <!-- Submit -->
            <div class="flex justify-end pt-2">
              <button
                type="button"
                class="ui-btn-accent !px-6 !py-3 !text-sm !gap-2 !rounded {!canSubmit || submitting ? 'opacity-40 cursor-not-allowed' : ''}"
                onclick={submit} disabled={!canSubmit || submitting}
              >
                {#if submitting}
                  <Icon icon="mdi:loading" class="w-4 h-4 animate-spin" /> {t("site_create.creating")}
                {:else}
                  <Icon icon="mdi:check" class="w-4 h-4" /> {t("site_create.create_site")}
                {/if}
              </button>
            </div>
          </div>
        </div>
      </div>
    {/if}
  {/if}
</Layout>
